'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Landmark, Users, TrendingUp, Euro, ArrowRight, MapPin, Search } from 'lucide-react';
import { formatNumber, formatCurrency } from '@/lib/gemeentes/utils';

interface Gemeente {
  naam: string;
  slug: string;
  grootte: 'klein' | 'middel' | 'groot';
  inwoners65Plus: number;
  aantalMetValrisico: number;
  zorgkostenVallenMiljoen: number;
  extraData?: {
    localRecognition?: {
      heroImages?: string[];
      provincie?: string;
    };
    localSeo?: {
      isPilotGemeente?: boolean;
    };
  };
}

interface GemeenteGridProps {
  gemeentes: Gemeente[];
}

const grootteLabelMap = {
  klein: 'Kleine gemeente',
  middel: 'Middelgrote gemeente',
  groot: 'Grote gemeente',
};

export default function GemeenteGrid({ gemeentes }: GemeenteGridProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredGemeentes = gemeentes.filter((gemeente) =>
    gemeente.naam.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {/* Search */}
      <div className="mb-8">
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink/40" />
          <input
            type="text"
            placeholder="Zoek uw gemeente..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-bg border border-ink/10 rounded-full text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-cta/20 focus:border-cta transition-all"
          />
        </div>
        {searchQuery && (
          <p className="mt-3 text-ink/60 text-sm">
            {filteredGemeentes.length} gemeente{filteredGemeentes.length !== 1 ? 'n' : ''} gevonden
          </p>
        )}
      </div>

      {/* Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredGemeentes.map((gemeente) => (
          <Link
            key={gemeente.slug}
            href={`/professionals/gemeenten/${gemeente.slug}`}
            className="group bg-bg rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300"
          >
            {/* Image */}
            <div className="aspect-[16/9] relative overflow-hidden">
              {gemeente.extraData?.localRecognition?.heroImages?.[0] ? (
                <Image
                  src={gemeente.extraData.localRecognition.heroImages[0]}
                  alt={gemeente.naam}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-cta/20 to-cta/5 flex items-center justify-center">
                  <Landmark className="w-16 h-16 text-cta/30" />
                </div>
              )}
              {/* Overlay with gemeente name */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-center gap-2 text-white/80 text-sm mb-1">
                  <MapPin className="w-4 h-4" />
                  {gemeente.extraData?.localRecognition?.provincie || 'Nederland'}
                </div>
                <h3 className="text-2xl font-semibold text-white">{gemeente.naam}</h3>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  gemeente.grootte === 'groot' ? 'bg-cta/20 text-cta' :
                  gemeente.grootte === 'middel' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {grootteLabelMap[gemeente.grootte]}
                </span>
                {gemeente.extraData?.localSeo?.isPilotGemeente && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    Pilotgemeente
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2 text-ink/60 text-sm mb-1">
                    <Users className="w-4 h-4" />
                    65-plussers
                  </div>
                  <div className="font-semibold text-ink">{formatNumber(gemeente.inwoners65Plus)}</div>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-ink/60 text-sm mb-1">
                    <TrendingUp className="w-4 h-4" />
                    Met valrisico
                  </div>
                  <div className="font-semibold text-ink">{formatNumber(gemeente.aantalMetValrisico)}</div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-ink/10">
                <div className="flex items-center gap-2 text-ink/60 text-sm">
                  <Euro className="w-4 h-4" />
                  {formatCurrency(gemeente.zorgkostenVallenMiljoen)}
                </div>
                <span className="text-cta font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  Bekijk
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* No results */}
      {filteredGemeentes.length === 0 && searchQuery && (
        <div className="text-center py-12">
          <Landmark className="w-16 h-16 text-ink/20 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-ink mb-2">Geen gemeenten gevonden</h3>
          <p className="text-ink/60">
            Probeer een andere zoekterm of neem contact op om uw gemeente toe te voegen.
          </p>
        </div>
      )}
    </>
  );
}
